/**
 * A formula tree, said out loud.
 *
 * "Litres or else the customer's Usual litres, times Rate or else the
 * customer's Usual rate." That sentence is the only thing most people will
 * ever see of a formula: it is the row under the field name in the customize
 * screen, the line under a link's mapping, the caption on an insight card, and
 * the body of an assistant's proposal. A screen that built its own wording
 * would say one of those four differently from the other three.
 *
 * So the wording lives HERE, beside the tree, for the reason the evaluator
 * does: one implementation is the only way the four can agree. It is a pure
 * function of the tree, a word table and a catalogue lookup, so it runs on the
 * server for a proposal's `describe` and on the device for a live preview.
 *
 * Every word comes through `t`. Nothing in this file is English, which is the
 * rule Section 1.1 keeps: a spec, a row and a screen carry keys, and the
 * catalogue carries the words. The one thing that is not a key is a field's
 * label, because that is the person's own word for their own field.
 */

import type { TOptions } from 'i18next';

import { isDateUnit, type FormulaNode } from './formula';

/**
 * The row a line's hop up the tree lands on (its parent or its root entry), as
 * the sentence names it: by the record's noun for one entry when it has one of
 * its own ("of the stay"), else by the record's name ("of Stays").
 */
export type FormulaHopRow = { readonly noun: string } | { readonly name: string };

/**
 * How the sentence asks for a name.
 *
 * `own` answers the label of a field on this record. `hop` answers the two
 * labels a hop needs — the reference field and the field on the other side —
 * and null when it cannot, which happens the moment a field is archived. A
 * hop through a line's parent or root field answers `row` too, and then the
 * sentence reads the field with the row it lands on: "Check in of the stay".
 * Both are synchronous, because a sentence is drawn in a render pass.
 */
export type FormulaWordLookup = {
  readonly own: (fieldId: string) => string | null;
  readonly hop: (via: string, fieldId: string) => { via: string; field: string; row?: FormulaHopRow } | null;
  /**
   * The label of a field on the rows an `agg` reads: the list a multi_select
   * points at, or the lines record of a `lines` field. Absent or null falls
   * back to the id.
   */
  readonly line?: (lines: string, fieldId: string) => string | null;
  /**
   * The word of one choice of a field of choices on this record, by the id an
   * entry stores: what a test compares it with ("Status is "Checked in"").
   * Absent or null keeps the value.
   */
  readonly choice?: (fieldId: string, value: string) => string | null;
};

/** The right side of a test: a choice of the field on the left by its own word, else the piece as it is said. */
const comparandOf = (left: FormulaNode, right: FormulaNode, words: FormulaWordLookup): string | null => {
  if (left.kind !== 'field' || right.kind !== 'const' || typeof right.value !== 'string') return null;
  const word = words.choice?.(left.fieldId, right.value) ?? null;
  return word === null ? null : `"${word}"`;
};

/**
 * The tree as one line of plain words.
 *
 * A field nobody can name falls back to its id rather than to a blank. An id
 * on the screen is ugly and it is honest; a blank is neither, and it hides the
 * archived field that caused it.
 */
export const formulaSentence = (node: FormulaNode, words: FormulaWordLookup, t: Say): string =>
  say(node, words, t);

/**
 * The translator this module is handed, which is `tData` at every call site.
 *
 * It takes the options too: `formula.words.to_places` carries
 * `{{places, number}}` since the `%s` pass, and a one-argument translator
 * would have drawn the marker.
 */
type Say = (key: string, options?: TOptions) => string;

const say = (node: FormulaNode, words: FormulaWordLookup, t: Say): string => {
  const part = (kid: FormulaNode): string => say(kid, words, t);

  switch (node.kind) {
    case 'field':
      return words.own(node.fieldId) ?? node.fieldId;

    case 'hop': {
      const held = words.hop(node.via, node.fieldId);
      const via = held?.via ?? node.via;
      const field = held?.field ?? node.fieldId;
      const row = held?.row;
      if (row !== undefined) {
        return 'noun' in row
          ? t('formula.words.of_the_row', { field, row: row.noun })
          : t('formula.words.of_record', { field, record: row.name });
      }
      return `${via} ${t('formula.hop_arrow')} ${field}`;
    }

    case 'const':
      // A number is written as a number; anything else is quoted, so "Cash"
      // never reads as a field somebody forgot to pick. A yes-or-no answer (a
      // field's "show when" rule) says yes or no, not the code's true and false.
      if (typeof node.value === 'boolean') return t(node.value ? 'formula.words.yes' : 'formula.words.no');
      return typeof node.value === 'number' ? String(node.value) : `"${String(node.value)}"`;

    case 'calc': {
      // Brackets on the right and never on the left. The left spine is the
      // chain the builder draws flat, and a chain reads left to right without
      // them; a calc on the RIGHT is the nested block, and there the brackets
      // are the whole difference between two answers.
      const right = node.right.kind === 'calc' ? `(${part(node.right)})` : part(node.right);
      return `${part(node.left)} ${t(`formula.op.${node.op}_word`)} ${right}`;
    }

    case 'round':
      return `${t('formula.words.round')} ${part(node.of)} ${t('formula.words.to_places', { places: node.decimals })}`;

    case 'if': {
      const test = t(`formula.test.${node.op}`);
      const asked =
        node.right === undefined
          ? `${part(node.left)} ${test}`
          : `${part(node.left)} ${test} ${comparandOf(node.left, node.right, words) ?? part(node.right)}`;
      return `${t('formula.words.if')} ${asked} ${t('formula.words.then')} ${part(node.then)} ${t('formula.words.otherwise')} ${part(node.otherwise)}`;
    }

    case 'days_between':
      return `${t('formula.words.days_from')} ${part(node.from)} ${t('formula.words.to')} ${part(node.to)}`;

    case 'concat':
      return node.parts.map(part).join(` ${t('formula.words.joined_with')} `);

    case 'coalesce':
      return node.of.map(part).join(` ${t('formula.words.or_else')} `);

    case 'agg': {
      const lines = words.own(node.lines) ?? node.lines;
      if (node.op === 'count') return `${t('formula.words.agg_count')} ${lines}`;
      const of = node.of === undefined ? '' : (words.line?.(node.lines, node.of) ?? node.of);
      const lead =
        node.op === 'sum'
          ? t('formula.words.agg_sum')
          : node.op === 'min'
            ? t('formula.words.agg_min')
            : t('formula.words.agg_max');
      return `${lead} ${of} ${t('formula.words.agg_over')} ${lines}`;
    }

    case 'date_add': {
      // A constant unit says its own word; a unit read from a field says the field.
      const unit =
        node.unit.kind === 'const' && isDateUnit(node.unit.value)
          ? t(`formula.unit.${node.unit.value}`)
          : part(node.unit);
      const moved = `${part(node.date)} ${t('formula.words.plus')} ${part(node.amount)} ${unit}`;
      return node.validTill ? `${moved} ${t('formula.words.valid_till')}` : moved;
    }

    case 'today':
      return t('formula.words.today');

    default:
      // A tree from a newer version. A sentence that guessed would be worse
      // than one that admits it, and the caller draws the field anyway.
      return t('formula.words.unknown');
  }
};
